import React, { useState } from 'react';
import Button from './ui/Button';
import { getItemTypeTranslation } from '../utils/itemTranslations';
import Input from './ui/Input';

const AddItemModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  availableItems = [],
  itemTypeFilter = null,
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    itemId: '',
    enhancement: 0,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'enhancement' ? parseInt(value) || 0 : value)
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.itemId) {
      newErrors.itemId = 'Item é obrigatório';
    }

    if (formData.enhancement < 0 || formData.enhancement > 25) {
      newErrors.enhancement = 'Level deve estar entre 0 e 25';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      itemId: '',
      enhancement: 0,
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getFilteredItems = () => {
    if (!Array.isArray(availableItems)) return [];
    
    if (!itemTypeFilter) return availableItems;
    
    // Se for ACCESSORY, incluir BOOTS, GLOVES, BRACELET e ACCESSORY genérico
    if (itemTypeFilter === 'ACCESSORY') {
      return availableItems.filter(item => 
        ['BOOTS', 'GLOVES', 'BRACELET', 'ACCESSORY'].includes(item.type)
      );
    }
    
    // Caso contrário, filtrar pelo tipo específico
    return availableItems.filter(item => item.type === itemTypeFilter);
  };

  const getSelectedItem = () => {
    const filteredItems = getFilteredItems();
    return filteredItems.find(item => item.id === formData.itemId) || null;
  };

  if (!isOpen) return null;

  const selectedItem = getSelectedItem();
  const filteredItems = getFilteredItems();

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-white/10 shadow-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">
              {itemTypeFilter 
                ? `Adicionar ${getItemTypeTranslation(itemTypeFilter)}`
                : 'Adicionar Item'
              }
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full w-8 h-8 flex items-center justify-center transition-colors text-2xl"
              disabled={loading}
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="itemId" className="block text-sm font-medium text-gray-300 mb-1">
                Item
              </label>
              <select
                id="itemId"
                name="itemId"
                value={formData.itemId}
                onChange={handleChange}
                disabled={loading}
                className="bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all"
              >
                <option value="" className="bg-slate-800 text-white">
                  {itemTypeFilter 
                    ? `Selecione um(a) ${getItemTypeTranslation(itemTypeFilter).toLowerCase()}`
                    : 'Selecione um item'
                  }
                </option>
                {filteredItems.map((item) => (
                  <option key={item.id} value={item.id} className="bg-slate-800 text-white">
                    {item.name}
                  </option>
                ))}
              </select>
              {errors.itemId && (
                <p className="text-red-400 text-sm mt-1">{errors.itemId}</p>
              )}
            </div>

            <div>
              <label htmlFor="enhancement" className="block text-sm font-medium text-gray-300 mb-1">
                Level
              </label>
              <Input
                id="enhancement"
                name="enhancement"
                type="number"
                min="0"
                max="25"
                value={formData.enhancement}
                onChange={handleChange}
                disabled={loading}
                placeholder="0 = sem enhancement, 25 = máximo"
              />
              {errors.enhancement && (
                <p className="text-red-400 text-sm mt-1">{errors.enhancement}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Level aumenta os atributos do item (0-25)
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <div className="flex-1">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleClose}
                  disabled={loading}
                  fullWidth
                >
                  Cancelar
                </Button>
              </div>
              <div className="flex-1">
                <Button
                  type="submit"
                  loading={loading}
                  fullWidth
                >
                  {loading ? 'Adicionando...' : 'Adicionar Item'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};



export default AddItemModal;